<template>
  <div v-click-outside="close">
    <div class="SearchFieldMulti__wrapper--activator" @click="open">
      <div
        v-for="(city, i) in selected"
        :key="city.cityId"
        class="SearchFieldMulti"
        :style="{ borderLeft: `.6rem solid ${chipColors[i]}` }"
      >
        <div class="SearchFieldMulti__wrapper">
          <span class="SearchFieldMulti__label">Till</span>
          <span class="SearchFieldMulti__input__wrapper">{{ cityDisplayName(city.name) }}</span>
          <button
            class="SearchFieldMulti__remove icon-circle-cross"
            :aria-label="`Ta bort ${city.name}`"
            @click.stop="remove(city)"
          />
        </div>
      </div>

      <button
        class="abBtn abBtn--whiteBlue abBtn-addCity"
        @click.stop="open"
      >
        {{ $l('flightdataPage.ui.add') }}
        <span class="icon-circle-plus" />
      </button>

      <button
        v-if="selected.length > 1"
        class="abBtn abBtn--whiteRed abBtn-resetCities"
        @click.stop="reset"
      >
        {{ $l('flightdataPage.ui.clear') }}
        <span class="icon-circle-cross" />
      </button>
    </div>

    <div
      v-if="isOpen"
      ref="dropdownRef"
      class="comboBoxDropdown"
    >
      <BaseSearchInput
        ref="searchInputRef"
        v-model:input="searchTerm"
        :placeholder="searchPlaceholder"
        @keydown.enter="handleEnter"
      />
      <div class="">
        <template v-for="country in filteredCountries" :key="country.countryCode">
          <div
            class="comboBoxDropdown-country comboBoxDropdown-country--clickable"
            :class="{ 'comboBoxDropdown-country--selected': isCountrySelected(country) }"
            tabindex="0"
            role="button"
            @click="toggleCountry(country)"
            @keydown.enter="toggleCountry(country)"
            @keydown.space.prevent="toggleCountry(country)"
          >
            <span class="comboBoxDropdown-country-name">{{ country.name }}</span>
          </div>
          <div
            v-for="city in country.cities"
            :key="city.cityId"
            class="comboBoxDropdown-city"
            :class="{ 'comboBoxDropdown-city--selected': isSelected(city) }"
            tabindex="0"
            role="button"
            @click="toggle(city)"
            @keydown.enter="toggle(city)"
            @keydown.space.prevent="toggle(city)"
          >
            {{ cityDisplayName(city.name) }}
          </div>
        </template>
        <div v-if="!filteredCountries.length" class="comboBoxDropdown-empty">
          {{ $l('showresult.filter.no_results') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { cityDisplayName, l, qsa, tabTrappingHelper } from '@utils/toolbox';
import BaseSearchInput from '@/src/vue/components/Base/BaseSearchInput.vue';

const props = defineProps({
	countries: {
		type: Array,
		default: () => []
	},
	maxDestinations: {
		type: Number,
		default: 5
	},
	initialSelected: {
		type: Array,
		default: () => []
	},
	chipColors: {
		type: Array,
		default: () => []
	}
});

const emit = defineEmits(['update:selected']);

const searchTerm = ref('');
const isOpen = ref(false);
const selected = ref(props.initialSelected);
const chipColors = ref(props.chipColors);
const searchInputRef = ref();
const dropdownRef = ref();

onMounted(() => {
	if (selected.value.length) {
		dispatchSelected();
	}
});
const searchPlaceholder = l('global.simplePhrases.search');

function getAlias (city) {
	return (window.js_params?.cityAliases?.[city.name] ?? '').toLowerCase();
}

function cityMatchesTerm (city, term) {
	return city.name.toLowerCase().includes(term) || getAlias(city).includes(term);
}

function cityStartsWithTerm (city, term) {
	return city.name.toLowerCase().startsWith(term) || getAlias(city).startsWith(term);
}

const filteredCountries = computed(() => {
	const term = searchTerm.value.toLowerCase();
	if (!term) {
		return props.countries;
	}

	const matched = props.countries.reduce((acc, country) => {
		// We show first complete match than just part of the city name
		const cities = country.cities.filter(city => cityMatchesTerm(city, term));
		const countryMatches = country.name.toLowerCase().includes(term);
		if (countryMatches || cities.length) {
			const cityScore = cities.some(city => cityStartsWithTerm(city, term)) ? 0 : 1;
			acc.push({ ...country, cities: countryMatches ? country.cities : cities, _score: cityScore });
		}
		return acc;
	}, []);

	matched.sort((a, b) => a._score - b._score);
	return matched;
});

const isSelected = city => selected.value.some(c => c.cityId === city.cityId);
const isCountrySelected = country => country.cities.every(c => isSelected(c));

function dispatchSelected () {
	emit('update:selected', selected.value);
	document.getElementById('flightdata-city-selector')?.dispatchEvent(
		new CustomEvent('fd:selected', { detail: selected.value, bubbles: true })
	);
}

function toggle (city) {
	const wasSelected = isSelected(city);
	const term = searchTerm.value.trim().toLowerCase();
	const matchedByCityTerm = !!term && cityMatchesTerm(city, term);

	if (wasSelected) {
		selected.value = selected.value.filter(c => c.cityId !== city.cityId);
	} else {
		selected.value = [...selected.value, city];
	}
	dispatchSelected();

	if (!wasSelected && matchedByCityTerm) {
		searchTerm.value = '';
	}
	searchInputRef.value?.focus();
}

function toggleCountry (country) {
	const allSelected = country.cities.every(c => isSelected(c));
	if (allSelected) {
		selected.value = selected.value.filter(c => !country.cities.some(cc => cc.cityId === c.cityId));
	} else {
		const toAdd = country.cities.filter(c => !isSelected(c));
		selected.value = [...selected.value, ...toAdd];
	}
	dispatchSelected();
	searchInputRef.value?.focus();
}

function handleEnter () {
	const term = searchTerm.value.trim().toLowerCase();
	if (!term) {
		return;
	}

	const matchedCountry = filteredCountries.value.find(country => country.name.toLowerCase() === term);
	if (matchedCountry) {
		const toAdd = matchedCountry.cities.filter(c => !isSelected(c));
		if (toAdd.length) {
			selected.value = [...selected.value, ...toAdd];
			dispatchSelected();
		}
		return;
	}

	const firstCity = filteredCountries.value.flatMap(country => country.cities)[0];
	if (firstCity && !isSelected(firstCity)) {
		toggle(firstCity);
	}
}

function remove (city) {
	selected.value = selected.value.filter(c => c.cityId !== city.cityId);
	dispatchSelected();
}

function reset () {
	selected.value = [];
	dispatchSelected();
	close();
}

function open () {
	isOpen.value = true;
	nextTick(() => {
		searchInputRef.value?.focus();

		if (dropdownRef.value) {
			const tabbableElements = qsa('input, [role="button"]', dropdownRef.value);
			tabTrappingHelper(dropdownRef.value, 'CitySelector', close, false, tabbableElements);
		}
	});
}

function close () {
	isOpen.value = false;
	searchTerm.value = '';
}

const vClickOutside = {
	mounted (el, binding) {
		el._clickOutside = (e) => {
			if (!el.contains(e.target)) {
				binding.value();
			}
		};
		document.addEventListener('click', el._clickOutside);
	},
	unmounted (el) {
		document.removeEventListener('click', el._clickOutside);
	}
};
</script>

<style lang="scss">

.SearchFieldMulti__wrapper--activator {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.SearchFieldMulti {
  height: 6rem;
  position: relative;
  border-radius: $borderRadius-xs;
}

.SearchFieldMulti__wrapper {
  position: relative;
  width: 100%;
  height: 6rem;
  padding: 1rem 5rem 1rem 1rem;
  background-color: #f1f4fa;

  @include breakpoint_up(md) {
    padding: 1rem 2rem;
  }
}

.SearchFieldMulti__label {
  display: block;
  white-space: nowrap;
  color: $color-grey-700;
  font-weight: 600;
  pointer-events: none;
  font-size: 1.4rem;
  line-height: 1.4rem;
}

.SearchFieldMulti__input__wrapper {
  display: flex;
  min-height: 3.5rem;
  max-width: 100%;
  align-items: center;
  font-size: 2rem;
  line-height: 2.2rem;
  font-weight: 600;
  color: $color-blue-800;
}

.SearchFieldMulti__remove {
  position: absolute;
  color: $color-red-300;
  top: .5rem;
  right: 0;
  background-color: unset;
  border: none;
  font-size: 2rem;
}

.comboBoxDropdown-country--clickable {
  cursor: pointer;
}

.flightdata__from-select {
  padding-right: 2rem;
}

.flightdata__from-select + .icon-chevron-down {
  margin-left: -2rem;
  pointer-events: none;
}

.abBtn-addCity,
.abBtn-resetCities {
  display: flex;
  gap: 1rem;
  align-items: center;
  max-height: 4rem;
  margin-top: 1rem;

  span {
    font-size: 2rem;
  }
}

</style>
